/**
 * @fileoverview ClubOS Player Profiles page — searchable player roster
 */

import { useState } from "react";

// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Collapse from "@mui/material/Collapse";

// ClubOS components
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftBadge from "components/SoftBadge";
import SoftAvatar from "components/SoftAvatar";
import SoftButton from "components/SoftButton";

// ClubOS examples
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

// Mock data
import { squad } from "data/mockData";

// India locale
import { formatCompact } from "utils/indiaLocale";

// TODO: Replace with API call when backend is ready
// import { squadService } from "api";

const POSITIONS = ["All", "GK", "CB", "LB", "RB", "CDM", "CM", "CAM", "LW", "RW", "ST"];
const STATUS_COLOR = { Available: "success", Injured: "error", Suspended: "warning", International: "info" };

function PlayerProfiles() {
  const [search, setSearch] = useState("");
  const [posFilter, setPosFilter] = useState("All");
  const [expanded, setExpanded] = useState(null);

  // TODO: Uncomment when API is ready
  // const [players, setPlayers] = useState(squad);
  // useEffect(() => { squadService.getSquad().then(setPlayers).catch(console.error); }, []);

  const filtered = squad.filter((p) => {
    const matchSearch = p.name.toLowerCase().includes(search.toLowerCase()) ||
                        p.nationality.toLowerCase().includes(search.toLowerCase());
    const matchPos = posFilter === "All" || p.position === posFilter;
    return matchSearch && matchPos;
  });

  const toggleExpand = (id) => setExpanded(expanded === id ? null : id);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox py={3}>
        <SoftBox mb={3}>
          <SoftTypography variant="h4" fontWeight="bold" color="dark">
            Player Profiles
          </SoftTypography>
          <SoftTypography variant="body2" color="text">
            Full roster · FC Metropolitan
          </SoftTypography>
        </SoftBox>

        <Card>
          <SoftBox p={3}>
            {/* Filters */}
            <Grid container spacing={2} mb={3} alignItems="center">
              <Grid item xs={12} sm={7}>
                <TextField
                  fullWidth
                  placeholder="Search by name or nationality..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Position</InputLabel>
                  <Select value={posFilter} label="Position" onChange={(e) => setPosFilter(e.target.value)}>
                    {POSITIONS.map((p) => <MenuItem key={p} value={p}>{p}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={2}>
                <SoftTypography variant="caption" color="text">{filtered.length} players</SoftTypography>
              </Grid>
            </Grid>

            {/* Table */}
            <SoftBox sx={{ overflowX: "auto" }}>
              <SoftBox component="table" sx={{ width: "100%", borderCollapse: "collapse", minWidth: 800 }}>
                <SoftBox component="thead" sx={{ backgroundColor: "#f8f9fa" }}>
                  <SoftBox component="tr">
                    {["Player", "Pos", "Age", "Nationality", "Contract End", "Market Value", "Status", ""].map((h) => (
                      <SoftBox key={h} component="th" sx={{ textAlign: "left", p: 1.5, fontWeight: 700, fontSize: "0.7rem", textTransform: "uppercase", color: "text.secondary", borderBottom: "2px solid #e9ecef" }}>
                        {h}
                      </SoftBox>
                    ))}
                  </SoftBox>
                </SoftBox>
                <SoftBox component="tbody">
                  {filtered.map((p, i) => (
                    <>
                      <SoftBox
                        key={`row-${p.id}`}
                        component="tr"
                        sx={{ cursor: "pointer", "&:hover": { backgroundColor: "#f8f9fa" }, backgroundColor: expanded === p.id ? "#eef6ff" : i % 2 === 0 ? "white" : "#fdfdfd" }}
                        onClick={() => toggleExpand(p.id)}
                      >
                        <SoftBox component="td" sx={{ p: 1.5, borderBottom: "1px solid #f0f0f0" }}>
                          <SoftBox display="flex" alignItems="center" gap={1}>
                            <SoftAvatar sx={{ bgcolor: "#e3f2fd", color: "#1565c0", fontSize: "0.7rem", width: 32, height: 32 }}>
                              {p.name.split(" ").map((n) => n[0]).join("").substring(0, 2)}
                            </SoftAvatar>
                            <SoftTypography variant="button" fontWeight="medium">{p.name}</SoftTypography>
                          </SoftBox>
                        </SoftBox>
                        <SoftBox component="td" sx={{ p: 1.5, borderBottom: "1px solid #f0f0f0" }}>
                          <SoftBadge badgeContent={p.position} color="info" size="xs" container />
                        </SoftBox>
                        <SoftBox component="td" sx={{ p: 1.5, borderBottom: "1px solid #f0f0f0" }}>
                          <SoftTypography variant="caption" color="text">{p.age}</SoftTypography>
                        </SoftBox>
                        <SoftBox component="td" sx={{ p: 1.5, borderBottom: "1px solid #f0f0f0" }}>
                          <SoftTypography variant="caption" color="text">{p.flag} {p.nationality}</SoftTypography>
                        </SoftBox>
                        <SoftBox component="td" sx={{ p: 1.5, borderBottom: "1px solid #f0f0f0" }}>
                          <SoftTypography variant="caption" color="text">{p.contractExpiry}</SoftTypography>
                        </SoftBox>
                        <SoftBox component="td" sx={{ p: 1.5, borderBottom: "1px solid #f0f0f0" }}>
                          <SoftTypography variant="button" fontWeight="medium">
                            {formatCompact(p.marketValue)}
                          </SoftTypography>
                        </SoftBox>
                        <SoftBox component="td" sx={{ p: 1.5, borderBottom: "1px solid #f0f0f0" }}>
                          <SoftBadge badgeContent={p.status} color={STATUS_COLOR[p.status]} size="xs" container />
                        </SoftBox>
                        <SoftBox component="td" sx={{ p: 1.5, borderBottom: "1px solid #f0f0f0" }}>
                          <SoftTypography variant="caption" color="info">
                            {expanded === p.id ? "▲ Hide" : "▼ Details"}
                          </SoftTypography>
                        </SoftBox>
                      </SoftBox>
                      {expanded === p.id && (
                        <SoftBox key={`detail-${p.id}`} component="tr">
                          <SoftBox component="td" colSpan={8} sx={{ backgroundColor: "#f7fbff", px: 3, py: 2, borderBottom: "1px solid #e9ecef" }}>
                            <Grid container spacing={3}>
                              <Grid item xs={12} sm={4}>
                                <SoftTypography variant="caption" color="text" fontWeight="bold" display="block">SQUAD GROUP</SoftTypography>
                                <SoftTypography variant="button">{p.squad}</SoftTypography>
                              </Grid>
                              <Grid item xs={12} sm={4}>
                                <SoftTypography variant="caption" color="text" fontWeight="bold" display="block">MONTHLY SALARY</SoftTypography>
                                <SoftTypography variant="button">{formatCompact(p.monthlySalary)}</SoftTypography>
                              </Grid>
                              <Grid item xs={12} sm={4}>
                                <SoftButton variant="outlined" color="info" size="small">
                                  View Full Profile
                                </SoftButton>
                              </Grid>
                            </Grid>
                          </SoftBox>
                        </SoftBox>
                      )}
                    </>
                  ))}
                </SoftBox>
              </SoftBox>
            </SoftBox>
          </SoftBox>
        </Card>
      </SoftBox>
      <Footer company={{ href: "#", name: "FC Metropolitan" }} links={[]} />
    </DashboardLayout>
  );
}

export default PlayerProfiles;
